/**
 * Mime types the browser canvas can re-encode as-is. A raster image in any
 * other format that still needs downscaling is rasterized to PNG.
 */
export const RESIZABLE_IMAGE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp'
]

/**
 * Vector images scale on their own, so their intrinsic size is irrelevant and
 * rasterizing them would be a regression. They are never resized.
 */
export const VECTOR_IMAGE_MIME_TYPES = ['image/svg+xml']

/**
 * @param {string} mimeType
 * @return {boolean}
 */
export function isImageMimeType(mimeType) {
  return typeof mimeType === 'string' && mimeType.startsWith('image/')
}

/**
 * Decides how an image of the given intrinsic size must be downscaled to fit
 * within maxDimension on both sides, and how the resulting file should be
 * named and encoded. The aspect ratio is always preserved.
 *
 * Pure geometry only: no DOM, no canvas — the caller applies the plan.
 *
 * @param {{width: number, height: number, type: string, name: string, maxDimension: number}} image
 * @return {{resized: boolean, width: number, height: number, type: string, name: string}}
 */
export function planImageResize({ width, height, type, name, maxDimension }) {
  const fitsAlready = width <= maxDimension && height <= maxDimension

  if (fitsAlready || VECTOR_IMAGE_MIME_TYPES.includes(type)) {
    return { resized: false, width, height, type, name }
  }

  const ratio = Math.min(maxDimension / width, maxDimension / height)
  const keepsEncoding = RESIZABLE_IMAGE_MIME_TYPES.includes(type)

  return {
    resized: true,
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
    type: keepsEncoding ? type : 'image/png',
    name: keepsEncoding ? name : `${name.replace(/\.[^.]+$/, '')}.png`
  }
}
